import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { FormTextInput } from "@nova/component-form-text-input"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { FunctionComponent, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import * as Icons from "@nova/component-icons"
import { hideGlobalModal } from "runlevel/actions"
import { CalTopoAuthorizationError, useAuthorizeCalTopo } from "./useAuthorizeCalTopo"

const CalTopoAuthModal: FunctionComponent = () => {
    const [credentialId, setCredentialId] = useState("")
    const [secret, setSecret] = useState("")
    const [map, setMap] = useState("")
    const [showSecret, setShowSecret] = useState(false)
    const [error, setError] = useState<CalTopoAuthorizationError>()

    const workspaceId = useAppSelector(selectActiveWorkspaceId())

    const dispatch = useAppDispatch()

    const handleAuthCreate = () => {
        dispatch(
            setGlobalNotificationContext({
                message: `Connected to CalTopo.`,
                status: GlobalNotificationStatus.success,
                timeoutInMs: 3000,
            })
        )
        dispatch(hideGlobalModal())
    }

    const handleAuthError = (e: CalTopoAuthorizationError) => {
        if (e.cause === "network") {
            dispatch(
                setGlobalNotificationContext({
                    message: `Failed to connect to CalTopo.`,
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 3000,
                })
            )
            return
        }
        setError(e)
    }

    const { authorizeCalTopo, isMutating } = useAuthorizeCalTopo(handleAuthCreate, handleAuthError)

    const handleAuthClick = () => {
        if (workspaceId) authorizeCalTopo({ credentialId, secret, map, workspaceId })
    }

    return (
        <HighPriorityNovaModalContainer
            title="Connect to CalTopo"
            className="w-[30rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={() => dispatch(hideGlobalModal())} />
                    <ButtonPrimary
                        title="Connect"
                        isLoading={isMutating}
                        onClick={handleAuthClick}
                        RightIcon={Icons.ArrowRight}
                        disabled={!credentialId || !secret || !map}
                    />
                </div>
            }
            closeModal={() => dispatch(hideGlobalModal())}
        >
            <div className="flex flex-col gap-6">
                <small className="text-gray-200">
                    Find your Credential ID and Secret in CalTopo under Team Admin &rarr; Details.
                    The credential needs Update permission on the destination map.
                </small>
                <FormTextInput
                    id="caltopo-credential-id"
                    label="Credential ID"
                    name="caltopo-credential-id"
                    value={credentialId}
                    onChange={(e) => setCredentialId(e.target.value)}
                    error={error?.cause === "credentials" ? error.message : undefined}
                />
                <FormTextInput
                    id="caltopo-secret"
                    label="Secret"
                    name="caltopo-secret"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    type={showSecret ? "text" : "password"}
                    rightIcon={
                        <span
                            onClick={() => setShowSecret((prev) => !prev)}
                            className="text-gray-300 cursor-pointer"
                        >
                            {showSecret ? <Icons.VisibilityEye /> : <Icons.VisibilityEyeOff />}
                        </span>
                    }
                />
                <FormTextInput
                    id="caltopo-map"
                    label="Map ID or URL"
                    name="caltopo-map"
                    value={map}
                    onChange={(e) => setMap(e.target.value)}
                    placeholder="https://caltopo.com/m/ABC123"
                    error={error?.cause === "map" ? error.message : undefined}
                />
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export { CalTopoAuthModal }
